import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  Flex,
  message,
  Table,
  Tooltip,
  Row,
  Col,
  Form,
  Typography,
} from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CustomModalWithoutButton,
  CustomParagraph,
  CustomPagination,
  PageHeaderActions,
} from '../../components';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext.jsx';
import mailIcon from '../../assets/images/mail.svg';
import phoneIcon from '../../assets/images/phone.svg';
import CommonDrawer from '../../components/CommonDrawer';
import CustomDeleteModal from '../Modals/CustomDeleteModal.jsx';
import StatusTag from '../../components/StatusTag.jsx';
import TransportProviderForm from './TransportProviderForm.jsx';
import ProviderDetails from './TransportProviderDetails.jsx';
import TransportConnectionModal from '../Modals/TransportConnectionModal.jsx';
import { DeleteCourierAccount } from '../../apis/onboarding.js';
import {
  GetCourierContactsDirectory,
  GetSettingsNewCouriers,
  GetSettingsNewCouriersById,
  LinkSettingsCourierAccount,
} from '../../apis/settings.js';
import TeamBadge from '../../components/TeamBadge.jsx';
import { getProviderImagePath } from '../../common/utils/imageUtils';
import ZoomlogiMark from '../../assets/images/zoomlogi-mark.svg';
import Arrows from '../../assets/images/arrows.png';
import { getPermissions } from '../../common/helper/getPermissions';
import {
  fetchAllCouriers,
  selectCouriersState,
} from '../../redux-toolkit/slice/onboardingSlice';
import useUnauthorize from '../../hooks/useUnauthorize';
import AddNewContactForm from './DirectContactList/AddNewContactForm';
import AddNoteScreen from './DirectContactList/AddNoteScreen';
import {
  mapApiContactToContact,
  mapApiToFormValues,
} from './DirectContactList/utils';
import DirectoryNoteScreen from './DirectContactList/DirectoryNoteScreen';
import DirectoryEditContactScreen from './DirectContactList/DirectoryContactEditScreen';
import theme from '@/assets/theme';
import { useTranslation } from 'react-i18next';
import { getTeamsDDLOFFeature } from '../../common/utils/dropDown';
import { isOtherTransportProvider } from '../../common/utils/isOtherTransportProvider';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';
import { useTableScroll } from '../../hooks/useTableScroll';
import { useQuotingVendorsGate } from '@/hooks/useQuotingVendorsGate';
import { useVendorConnections } from './VendorConnections/hooks/useVendorConnections';
import VendorConnectionForm from './VendorConnections/components/VendorConnectionForm';
import { vendorRowsFor } from './VendorConnections/vendorRows';
import './TransportProviders.css';

const TransportProviders = ({ title }) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const tableScroll = useTableScroll();
  const allTeams = useSelector((state) => state.teams.list);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { showSpin, hideSpin } = useSpin();
  const [providersList, setProvidersList] = useState([]);
  const [isAddingSupport, setIsAddingSupport] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [recordId, setRecordID] = useState(null);
  const [courierID, setCourierID] = useState(null);
  const [modalState, setModalState] = useState({
    delete: false,
    connection: false,
  });
  const main_menu = useSelector((state) => state.menu.items);
  const CouriersPermissions = getPermissions(
    main_menu,
    'Settings',
    'Transport providers'
  );
  const [drawerView, setDrawerView] = useState('');
  // "Quoting and Booking" vendors (Freightos, Cargo One) in the Add drawer;
  // released behind a Statsig gate, idle (no request) while it is off.
  // Only users who can add providers see or manage vendors (the API enforces it).
  const quotingVendorsEnabled =
    useQuotingVendorsGate() && Boolean(CouriersPermissions?.is_create);
  const vendorConnections = useVendorConnections(quotingVendorsEnabled);
  // a stable setter, so it can sit in the dependency arrays below
  const setVendorEditing = vendorConnections.setEditing;
  const [contactForm] = Form.useForm();
  const [detailsForm] = Form.useForm();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const dispatch = useDispatch();
  const { courierList } = useSelector(selectCouriersState);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [maxPageSize, setMaxPageSize] = useState(0);
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteInitial, setNoteInitial] = useState('');
  const location = useLocation();
  const [selectedTeams, setSelectedTeams] = useState([]);
  const queryParams = new URLSearchParams(location.search);
  const openDrawer = queryParams.get('openDrawer') === 'true';
  const TPId = queryParams.get('id');
  const [workingContacts, setWorkingContacts] = useState([]);

  const upsertDraftContact = (apiContact) => {
    setWorkingContacts((prev) => {
      const id = String(apiContact?.id ?? '');
      const next = [...(prev ?? [])];
      const idx = next.findIndex((c) => String(c.id) === id);

      const newNote =
        apiContact?.notes ??
        apiContact?.note ??
        apiContact?.usage_notes ??
        undefined;

      if (idx >= 0) {
        const old = next[idx];
        const mergedApi = {
          ...old.api,
          ...apiContact,
          ...(newNote !== undefined ? { notes: newNote } : {}),
        };
        const remapped = mapApiContactToContact(mergedApi);
        next[idx] = {
          ...old,
          ...remapped,
          ...(newNote !== undefined ? { note: newNote } : {}),
          api: mergedApi,
        };
      } else {
        const mapped = mapApiContactToContact({
          ...apiContact,
          ...(newNote !== undefined ? { notes: newNote } : {}),
        });
        next.push(mapped);
      }
      return next;
    });
  };

  const initialContactType =
    editingContact?.contact_type === 'general_support' || isAddingSupport
      ? 'support'
      : 'direct_contact';

  const [contactType, setContactType] = useState(initialContactType);

  const handleEditFromList = useCallback((apiContact, opts) => {
    if (opts?.source === 'directory') {
      setEditingContact(mapApiToFormValues(apiContact));
      setDrawerView('editContactDirectory');
      return;
    }

    const isSupport = apiContact.contact_type === 'general_support';
    setContactType(isSupport ? 'support' : 'direct_contact');
    if (isSupport) setContactType('support');

    setIsAddingSupport(apiContact?.contact_type === 'general_support');
    setEditingContact(mapApiToFormValues(apiContact));
    setDrawerView('addContact');
  }, []);
  const handleOpenAddNote = useCallback((contact, opts) => {
    if (!contact) return;
    setNoteTarget(contact);
    setNoteInitial(
      contact?.note ?? contact?.api?.usage_notes ?? contact?.message ?? ''
    );
    if (opts?.source === 'directory') {
      setDrawerView('addNoteDirectory');
    } else {
      setDrawerView('addNote');
    }
  }, []);

  const handleSaveNote = (newNote) => {
    setWorkingContacts((prev) =>
      (prev ?? []).map((c) =>
        String(c.id) === (noteTarget ? String(noteTarget.id) : '')
          ? {
              ...c,
              note: newNote,
              api: c.api ? { ...c.api, notes: newNote } : c.api,
            }
          : c
      )
    );
    setNoteTarget((nt) => (nt ? { ...nt, note: newNote } : nt));
    setDrawerView('details');
  };
  const [providerDetails, setProviderDetails] = useState(null);

  const fetchCourierContacts = async (token, courierId) => {
    if (!token || !courierId) return;

    try {
      showSpin();

      const res = await GetCourierContactsDirectory(token, courierId);
      const apiContacts = res.data || [];
      const mapped = apiContacts.map(mapApiContactToContact);
      const defaultContacts = mapped.filter(
        (contact) => contact.default === true
      );

      setWorkingContacts(defaultContacts);
    } catch (err) {
      unauthorize(err);
    } finally {
      hideSpin();
    }
  };
  const loadProviderDetails = useCallback(
    async (id) => {
      try {
        showSpin();
        const res = await GetSettingsNewCouriersById(id, token);
        const data = res?.data?.data?.data?.object ?? null;
        setProviderDetails(data);
        const mapped = (data?.contact ?? [])
          .slice()
          .sort((a, b) => (a?.order_index ?? 0) - (b?.order_index ?? 0))
          .map(mapApiContactToContact);

        setWorkingContacts(mapped);
      } catch (error) {
        unauthorize(error);
      } finally {
        hideSpin();
      }
    },
    [token, showSpin, hideSpin, unauthorize]
  );

  const handleEdit = useCallback(
    (record) => {
      // a connected quoting/booking vendor row opens its connection form
      if (record?.vendor_connection) {
        setVendorEditing(record.vendor_connection);
        return;
      }
      let instruction = '';
      if (record && !record?.instructions) {
        instruction = courierList.find(
          (x) => x.id === record?.courier_id
        )?.instructions;
        record = { ...record, instructions: instruction };
      }
      setSelectedProvider(record);
      setDrawerView('details');
      setIsEditing(true);
      setDrawerOpen(true);
      setRecordID(record?.id ?? null);
      setCourierID(record?.courier_id ?? null);
      loadProviderDetails(record?.id);
    },
    [courierList, loadProviderDetails, setVendorEditing]
  );

  useEffect(() => {
    if (openDrawer && TPId && providersList?.length > 0) {
      const record = providersList?.find((item) => item?.id === TPId);
      if (record) {
        handleEdit(record);
      }
      const cleanedPath = location.pathname;
      navigate(cleanedPath, { replace: true });
    }
  }, [
    openDrawer,
    TPId,
    providersList,
    handleEdit,
    location.pathname,
    navigate,
  ]);

  const addFormRef = useRef(null);
  const handleBack = () => {
    contactForm.resetFields();
    setEditingContact(null);

    if (drawerView === 'addNote') {
      setDrawerView('details');
      return;
    }
    if (drawerView === 'addContact') {
      if (editingContact) {
        setDrawerView('details');
        return;
      }
      if (addFormRef.current?.isSupportFormActive) {
        addFormRef.current.backFromSupport();
        return;
      }
      setDrawerView('details');
      return;
    }
    setDrawerView('details');
  };
  const GetAllCouriers = useCallback(() => {
    showSpin();
    GetSettingsNewCouriers(token, page, search)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            if (data?.data?.data?.couriers) {
              setProvidersList(data?.data?.data?.couriers);
              setTotalPages(data?.data?.page_count || 0);
              setMaxPageSize(data?.data?.total_count || 0);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [hideSpin, page, search, showSpin, token, unauthorize]);

  useEffect(() => {
    GetAllCouriers();
  }, [GetAllCouriers, page, search]);

  useEffect(() => {
    if (token && courierList.length === 0) {
      dispatch(fetchAllCouriers(token));
    }
  }, [token, dispatch, courierList.length]);

  const handleDeleteClick = (record) => {
    if (record?.vendor_connection) {
      vendorConnections.setDisconnectTarget(record.vendor_connection);
      return;
    }
    setRecordID(record.id);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    showSpin();

    setDeleteModalVisible(false);
    setDrawerOpen(false);
    setModalState((prev) => ({ ...prev, delete: false }));

    if (recordId) {
      showSpin();
      DeleteCourierAccount(recordId, token)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              message.success(data?.data?.message);
              setSelectedProvider(null);
              GetAllCouriers();
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  const handleCloseDrawer = () => {
    setSelectedTeams([]);
    setProviderDetails(null);
    detailsForm?.resetFields();
    contactForm?.resetFields();
    setWorkingContacts([]);
    setModalOpen(false);
    setDrawerOpen(false);
    setSelectedProvider(null);
    setSearchQuery('');
    GetAllCouriers();
  };

  const onModalConfirm = async (data) => {
    const provider = selectedProvider;
    const isOther = isOtherTransportProvider(provider);

    if (isOther && data?.value === 'new') {
      showSpin();
      try {
        const teamsList = await getTeamsDDLOFFeature(
          token,
          'Transport providers'
        );
        const defaultTeam = teamsList?.find(
          (tm) => tm?.name?.trim().toLowerCase() === 'default'
        );
        if (!defaultTeam?.id) {
          message.error(t('otherTransportProviderNoDefaultTeam'));
          setModalOpen(false);
          setModalState((prev) => ({ ...prev, connection: false }));
          setSelectedProvider((prev) => ({ ...prev, ...data }));
          setDrawerView('details');
          return;
        }

        const credValues = data?.credentials
          ? Object.values(data.credentials)
          : [];
        const description = credValues.filter(Boolean).join(', ');

        let contacts = (workingContacts ?? []).map((c, i) => ({
          ...c.api,
          order_index: i,
        }));
        if (Array.isArray(contacts)) {
          contacts = contacts.map((contact) => {
            if (contact?.default === true) {
              return { ...contact, contact_type: 'general_support' };
            }
            return contact;
          });
        }

        const model = {
          name: provider?.title || provider?.courier_name || 'Other',
          description,
          teams: [defaultTeam.id],
          contacts,
          courier: courierID,
          credentials: data.credentials,
        };

        const res = await LinkSettingsCourierAccount(token, model);
        const payload = res?.data ?? null;
        if (payload?.data?.is_success) {
          message.success(
            theme.themeType === 2
              ? t('otherTransportProviderSaveSuccessCarrier')
              : t('otherTransportProviderSaveSuccess')
          );
          handleCloseDrawer();
        } else {
          message.error(payload?.data?.message || t('pleaseSelectValue'));
        }
      } catch (err) {
        unauthorize(err);
      } finally {
        hideSpin();
      }
      return;
    }

    setModalOpen(false);
    setModalState((prev) => ({ ...prev, connection: false }));
    setSelectedProvider((prev) => ({ ...prev, ...data }));
    // the drawer reads providerDetails first when editing, so it needs the new credentials too
    setProviderDetails((prev) => (prev ? { ...prev, ...data } : prev));
    setDrawerView('details');
  };

  const handleSelectQuotingVendor = (vendor) => {
    setDrawerOpen(false);
    setDrawerView('');
    setSearchQuery('');
    vendorConnections.setEditing(vendor);
  };

  const handleAddProvider = () => {
    setSelectedProvider(null);
    setIsEditing(false);
    setDrawerView('form');
    setDrawerOpen(true);
  };

  const isLargeScreen = window.innerWidth >= 1690;
  const isMiddleScreen = window.innerWidth < 1600 && window.innerWidth > 1380;
  const columns = [
    {
      title: `${theme.themeType === 2 ? t('carrier') : t('provider')}`,
      dataIndex: 'courier_name',
      key: 'courier_name',
      render: (text) => (
        <div>
          <CustomParagraph
            noText='--'
            text={text}
            fontSize={12}
            fontWeight={600}
            color='var(--text-primary)'
          />
        </div>
      ),
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      width: isLargeScreen ? undefined : isMiddleScreen ? 200 : 120,

      render: (text) => (
        <div className='min-width-name'>
          <Typography.Text
            ellipsis={{
              tooltip: true,
            }}
          >
            {text}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status, record) => (
        <div>
          <StatusTag
            status={status}
            title={status === 'ERROR' ? record.status_detail : undefined}
          />
        </div>
      ),
    },
    {
      title: `${theme.themeType === 2 ? t('client') : t('team')}`,
      dataIndex: 'teams',
      key: 'teams',

      render: (_, record) => {
        return (
          <div className='transport-providers-teams-cell'>
            {record?.teams?.length > 0 ? (
              <div className='transport-providers-teams-row'>
                <TeamBadge
                  color={record?.teams[0]?.color}
                  text={record?.teams[0]?.name}
                  tooltipText={record?.teams[0]?.name}
                />
                {record?.teams.length > 1 && (
                  <Tooltip
                    title={record.teams
                      .slice(1)
                      .map((team) => team.name)
                      .join(', ')}
                    placement='top'
                  >
                    <div className='transport-providers-team-more'>
                      <TeamBadge
                        color={null}
                        text={`+ ${record?.teams.length - 1}`}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            ) : (
              <div>--</div>
            )}
          </div>
        );
      },
    },
    {
      title: t('primaryContact'),
      dataIndex: 'contact',
      key: 'contact',
      render: (contact) => {
        const primaryContact = contact?.find((c) => c.is_primary_contact);

        return (
          <div>
            <Flex gap={8} align='center'>
              {primaryContact ? (
                <>
                  <CustomParagraph
                    text={
                      primaryContact?.name ||
                      primaryContact?.contact_display_name
                    }
                    fontSize={14}
                    fontWeight={400}
                    color='var(--text-primary)'
                  />
                  <div className='transport-providers-contact-row'>
                    {primaryContact?.phone_number && (
                      <Tooltip title={primaryContact.phone_number}>
                        <img
                          src={phoneIcon}
                          alt='Phone'
                          className={`transport-providers-contact-icon transport-providers-contact-icon--phone${
                            primaryContact?.email
                              ? ' transport-providers-contact-icon--spaced'
                              : ''
                          }`}
                        />
                      </Tooltip>
                    )}

                    {primaryContact?.email && (
                      <Tooltip title={primaryContact.email}>
                        <img
                          src={mailIcon}
                          alt='Mail'
                          className='transport-providers-contact-icon'
                        />
                      </Tooltip>
                    )}
                  </div>
                </>
              ) : (
                <CustomParagraph text='--' fontSize={12} color='#888' />
              )}
            </Flex>
          </div>
        );
      },
    },

    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Flex align='center' gap={6}>
          {record?.permissions?.is_update && (
            <Flex
              justify='center'
              align='center'
              className='action-icon-wrapper'
              role='button'
              aria-label='Edit'
              onClick={() => handleEdit(record)}
            >
              <PencilIcon color='var(--text-primary)' />
            </Flex>
          )}
          {record?.permissions?.is_delete && (
            <>
              <Flex
                justify='center'
                align='center'
                className='action-icon-wrapper'
                role='button'
                aria-label='Delete'
                onClick={() => handleDeleteClick(record)}
              >
                <DeleteIcon />
              </Flex>
            </>
          )}
        </Flex>
      ),
    },
  ];

  const handleSearchChange = (value = '') => {
    setSearch(value);
    setPage(1);
  };

  const handlePageSizeChange = (newPageSize) => {
    if (!newPageSize || newPageSize < 1) return;
    if (newPageSize > 200) newPageSize = 200;

    setPageSize(newPageSize);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
  };
  const handleProviderSelect = (provider) => {
    setCourierID(provider?.id);
    setSelectedProvider(provider);

    if (provider?.id && token) {
      fetchCourierContacts(token, provider.id);
    }
  };
  // Connected quoting/booking vendors list beside the providers (first page, so
  // they are not repeated on every page); the counts include them.
  const vendorRows = vendorRowsFor(vendorConnections.vendors, search);
  const tableRows =
    page === 1 ? [...providersList, ...vendorRows] : providersList;
  const vendorCount = vendorRows.length;
  const totalCount = maxPageSize + vendorCount;
  const vendorOffset = page > 1 ? vendorCount : 0;
  const rangeStart =
    totalCount === 0 ? 0 : (page - 1) * pageSize + 1 + vendorOffset;
  const rangeEnd =
    totalCount === 0 ? 0 : Math.min(page * pageSize + vendorCount, totalCount);
  const TeamsFilteredColumns =
    allTeams?.length > 0
      ? columns
      : columns.filter((column) => column.key !== 'teams');

  const isOtherProviderModal = isOtherTransportProvider(selectedProvider);

  // Resolve the base Courier for the selected provider (handles both new-add and
  // change-credentials flows, where selectedProvider may be a CompanyCourier).
  const _sourceCourier = selectedProvider?.courier_id
    ? courierList.find((c) => c.id === selectedProvider.courier_id)
    : selectedProvider;
  const isSimpleProviderModal =
    !isOtherProviderModal && _sourceCourier?.instructions === 'awb_basic';
  const OCEAN_TITLES = new Set([
    'CMA CGM',
    'MSC',
    'Hapag Lloyd',
    'ONE',
    'HMM',
    'Maersk',
    'ICL',
  ]);
  const isOceanProviderModal =
    isSimpleProviderModal &&
    OCEAN_TITLES.has(
      selectedProvider?.title || selectedProvider?.courier_name || ''
    );

  const dismissTransportConnectionModal = () => {
    setModalOpen(false);
    setModalState((prev) => ({ ...prev, connection: false }));
  };

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          showToggle={false}
          title={`${theme.themeType === 2 ? tCommon('carriers') : title}`}
          totalCount={totalCount}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          addButtonText={t('add')}
          onSearchChange={handleSearchChange}
          onSearchClear={handleSearchChange}
          showAddButton={CouriersPermissions?.is_create}
          onAddClick={handleAddProvider}
        />
        <Table
          columns={TeamsFilteredColumns}
          dataSource={tableRows}
          pagination={false}
          className='settings-table transport-provider-table settings-table-action-cols'
          scroll={tableScroll}
          tableLayout='fixed'
        />
        {providersList.length > 0 && (
          <Row style={{ width: '100%', marginTop: '12px' }} justify='center'>
            <Col>
              <CustomPagination
                maxPageSize={maxPageSize}
                page={page}
                totalPages={totalPages}
                pageSize={50}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </Col>
          </Row>
        )}
      </div>
      <CustomDeleteModal
        visible={deleteModalVisible || modalState.delete}
        onClose={() => {
          setDeleteModalVisible(false);
          setModalState((prev) => ({ ...prev, delete: false }));
        }}
        onConfirm={handleConfirmDelete}
        title={
          modalState.delete
            ? `${t('removeConnection')} ${selectedProvider?.title || selectedProvider?.courier_name}`
            : `${theme.themeType === 2 ? t('removeCarrier') : t('removeTransportProvider')}`
        }
        message={
          modalState.delete
            ? `${theme.themeType === 2 ? t('carrierUsedInShipments') : t('providerUsedInShipments')}`
            : `${theme.themeType === 2 ? t('removeCarrierMessage') : t('removeProviderMessage')}`
        }
        cancelText={t('cancel')}
        confirmText={t('delete')}
        confirmColor='var(--danger)'
      />
      <CustomDeleteModal
        visible={Boolean(vendorConnections.disconnectTarget)}
        onClose={() => vendorConnections.setDisconnectTarget(null)}
        onConfirm={vendorConnections.handleConfirmDisconnect}
        title={`Remove ${vendorConnections.disconnectTarget?.display_name ?? 'vendor'}`}
        message='Its saved credentials are deleted and it is no longer searched. You can connect it again later.'
        cancelText={t('cancel')}
        confirmText={t('delete')}
        confirmColor='var(--danger)'
      />

      <CommonDrawer
        {...(['addContact', 'addNote'].includes(drawerView)
          ? { onBack: handleBack }
          : {})}
        open={drawerOpen}
        onClose={handleCloseDrawer}
      >
        {drawerView === 'addContact' && (
          <AddNewContactForm
            contactType={contactType}
            setContactType={setContactType}
            setWorkingContacts={setWorkingContacts}
            handleEditFromList={handleEditFromList}
            handleOpenAddNote={handleOpenAddNote}
            setIsAddingSupport={setIsAddingSupport}
            isAddingSupport={isAddingSupport}
            drawerOpen={drawerOpen}
            workingContacts={workingContacts}
            onUpsertDraft={upsertDraftContact}
            isEditing={isEditing}
            providerData={selectedProvider}
            selectedTeamIds={(selectedTeams ?? []).map((t) => t.id)}
            ref={addFormRef}
            title={editingContact ? t('edit') : t('add')}
            contactList={[]}
            form={contactForm}
            editContact={editingContact}
            setEditingContact={setEditingContact}
            onCancel={() => {
              contactForm.resetFields();
              setEditingContact(null);
              handleCloseDrawer();
            }}
            onSaved={() => {
              setIsAddingSupport(false);
              contactForm.resetFields();
              setEditingContact(null);
              setDrawerView('details');
            }}
          />
        )}
        {drawerView === 'addNoteDirectory' && (
          <DirectoryNoteScreen
            contact={noteTarget}
            defaultValue={noteInitial}
            onCancel={() => {
              setDrawerView('addContact');
              setContactType?.('support');
              setIsAddingSupport(false);
            }}
            onSave={(text) => {
              setWorkingContacts((prev) =>
                (prev ?? []).map((c) =>
                  String(c.id) === String(noteTarget?.id)
                    ? {
                        ...c,
                        note: text,
                        api: { ...(c.api ?? {}), usage_notes: text },
                      }
                    : c
                )
              );
              setDrawerView('addContact');
              setContactType?.('support');
              setIsAddingSupport(false);
            }}
          />
        )}
        {drawerView === 'editContactDirectory' && (
          <DirectoryEditContactScreen
            contact={editingContact}
            onCancel={() => {
              setDrawerView('addContact');
            }}
            onSave={(updated) => {
              setWorkingContacts((prev) =>
                (prev ?? []).map((c) =>
                  String(c.id) === String(editingContact?.id)
                    ? {
                        ...c,
                        note: updated?.note ?? c.note,
                        api: {
                          ...(c.api ?? {}),
                          usage_notes: updated?.note ?? c.api?.usage_notes,
                        },
                      }
                    : c
                )
              );
              setContactType?.('support');
              setDrawerView('addContact');
              setIsAddingSupport(false);
            }}
          />
        )}

        {(providerDetails || selectedProvider) && (
          <div
            className={
              drawerView === 'details'
                ? 'transport-providers-details-pane'
                : 'transport-providers-details-pane transport-providers-details-pane--hidden'
            }
          >
            <ProviderDetails
              drawerView={drawerView}
              setContactType={setContactType}
              handleEditFromList={handleEditFromList}
              handleOpenAddNote={handleOpenAddNote}
              form={detailsForm}
              setIsAddingSupport={setIsAddingSupport}
              setWorkingContacts={setWorkingContacts}
              workingContacts={workingContacts}
              upsertDraftContact={upsertDraftContact}
              mapApiContactToContact={mapApiContactToContact}
              editingContact={editingContact}
              setEditingContact={setEditingContact}
              selectedTeams={selectedTeams}
              setSelectedTeams={setSelectedTeams}
              title='provider'
              setNoteTarget={setNoteTarget}
              setNoteInitial={setNoteInitial}
              setModalState={setModalState}
              providerData={providerDetails || selectedProvider}
              onClose={handleCloseDrawer}
              edit={isEditing}
              setDrawerView={setDrawerView}
              recordId={recordId}
              courierId={courierID}
            />
          </div>
        )}
        {drawerView === 'form' && (
          <TransportProviderForm
            setSelectedProvider={handleProviderSelect}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            token={token}
            onClose={handleCloseDrawer}
            quotingVendors={vendorConnections.vendors}
            onSelectQuotingVendor={handleSelectQuotingVendor}
          />
        )}
        {drawerView === 'addNote' && (
          <AddNoteScreen
            contact={noteTarget}
            defaultValue={noteInitial}
            onCancel={handleBack}
            onSave={handleSaveNote}
          />
        )}
      </CommonDrawer>

      {vendorConnections.editing && (
        <CommonDrawer open onClose={() => vendorConnections.setEditing(null)}>
          <VendorConnectionForm
            vendor={vendorConnections.editing}
            saving={vendorConnections.saving}
            onSave={vendorConnections.handleSave}
            onCancel={() => vendorConnections.setEditing(null)}
          />
        </CommonDrawer>
      )}

      {modalState.connection || modalOpen ? (
        <CustomModalWithoutButton
          modalOpen={modalOpen || modalState.connection}
          setModalOpen={() => {
            setModalOpen(false);
            setModalState((prev) => ({ ...prev, connection: false }));
          }}
          modalTitle={
            isOtherProviderModal
              ? theme.themeType === 2
                ? t('otherTransportProviderModalTitleCarrier')
                : t('otherTransportProviderModalTitleTp')
              : isSimpleProviderModal
                ? `${t('connectTo')} ${selectedProvider?.title || selectedProvider?.courier_name}`
                : `${t('connectTo')} ${selectedProvider?.title || selectedProvider?.courier_name} ${t('connectForAdvancedTracking')}`
          }
          modalText={
            isOtherProviderModal
              ? theme.themeType === 2
                ? t('otherTransportProviderModalSubtitleCarrier')
                : t('otherTransportProviderModalSubtitle')
              : isOceanProviderModal
                ? t('bolTrackingMessage')
                : isSimpleProviderModal
                  ? t('awbTrackingMessage')
                  : t('apiAccessShipmentMessage')
          }
          modalSize={795}
          modalTextColor={
            isOtherProviderModal || isSimpleProviderModal
              ? '#5E5E5E'
              : undefined
          }
          modalBorderRadius={'16px'}
          headerIcon={
            selectedProvider &&
            !isOtherProviderModal && (
              <Flex align='center' justify='center' gap={12}>
                <Avatar
                  src={
                    <img
                      src={getProviderImagePath(
                        selectedProvider.title ||
                          selectedProvider?.courier_name,
                        'transportProvider'
                      )}
                      alt={
                        selectedProvider.name || selectedProvider?.courier_name
                      }
                      className='transport-providers-avatar-img'
                    />
                  }
                  size={52}
                  style={{
                    backgroundColor: 'transparent',
                    boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)',
                    borderRadius: '50%',
                  }}
                />
                <img
                  src={Arrows}
                  alt='connects to'
                  className='transport-providers-arrows'
                />
                <Avatar
                  size={52}
                  style={{
                    backgroundColor: 'transparent',
                    boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)',
                    borderRadius: '50%',
                  }}
                >
                  <div className='transport-providers-avatar-inner'>
                    <img
                      src={ZoomlogiMark}
                      alt='Zoomlogi'
                      className='transport-providers-avatar-img'
                    />
                  </div>
                </Avatar>
              </Flex>
            )
          }
        >
          <TransportConnectionModal
            data={selectedProvider}
            onSuccess={onModalConfirm}
            variant={
              isOtherProviderModal
                ? 'other'
                : isSimpleProviderModal
                  ? 'simple'
                  : 'default'
            }
            onDismiss={
              isOtherProviderModal || isSimpleProviderModal
                ? dismissTransportConnectionModal
                : undefined
            }
          />
        </CustomModalWithoutButton>
      ) : null}
    </>
  );
};

export default TransportProviders;
